var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable, runInAction } from "mobx";
import { NOT_LOADED } from "../../Infra/RemoteData";
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export class GlobalAdminReportSchedulesViewModel {
    constructor(container) {
        this.definitions = NOT_LOADED;
        this.errorMessage = null;
        this.editingReportType = null;
        this.isSaving = false;
        this.frequency = "DAILY";
        this.hour = 6;
        this.weekday = 1;
        this.dayOfMonth = 1;
        this.customCron = "";
        this.enabled = true;
        this.reportsAdminService = container.reportsAdminService;
        void this.load();
    }
    async load() {
        this.errorMessage = null;
        try {
            const definitions = await this.reportsAdminService.listDefinitions();
            runInAction(() => {
                this.definitions = definitions;
            });
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
    }
    get allReports() {
        return this.definitions === NOT_LOADED ? [] : this.definitions;
    }
    get scheduledCount() {
        return this.allReports.filter((d) => d.schedule !== undefined).length;
    }
    get activeCount() {
        return this.allReports.filter((d) => { var _a; return ((_a = d.schedule) === null || _a === void 0 ? void 0 : _a.enabled) === true; }).length;
    }
    get editingDefinition() {
        var _a;
        if (this.editingReportType === null)
            return null;
        return (_a = this.allReports.find((d) => d.type === this.editingReportType)) !== null && _a !== void 0 ? _a : null;
    }
    get cronPreview() {
        if (this.frequency === "CUSTOM")
            return this.customCron;
        const hour = clampHour(this.hour);
        switch (this.frequency) {
            case "DAILY":
                return `0 ${hour} * * *`;
            case "WEEKLY":
                return `0 ${hour} * * ${this.weekday}`;
            case "MONTHLY":
                return `0 ${hour} ${clampDayOfMonth(this.dayOfMonth)} * *`;
            default:
                return `0 ${hour} * * *`;
        }
    }
    get summary() {
        var _a;
        const hour = String(clampHour(this.hour)).padStart(2, "0");
        switch (this.frequency) {
            case "DAILY":
                return `Every day at ${hour}:00 Pacific`;
            case "WEEKLY":
                return `Every ${(_a = WEEKDAYS[this.weekday]) !== null && _a !== void 0 ? _a : "Monday"} at ${hour}:00 Pacific`;
            case "MONTHLY":
                return `Day ${clampDayOfMonth(this.dayOfMonth)} of each month at ${hour}:00 Pacific`;
            default:
                return this.customCron.trim().length > 0 ? this.customCron : "Enter a cron expression";
        }
    }
    startEditing(definition) {
        var _a, _b, _c;
        this.editingReportType = definition.type;
        this.errorMessage = null;
        const existing = definition.schedule;
        this.enabled = (_a = existing === null || existing === void 0 ? void 0 : existing.enabled) !== null && _a !== void 0 ? _a : true;
        this.frequency = "CUSTOM";
        this.customCron = (_c = (_b = existing === null || existing === void 0 ? void 0 : existing.cron) !== null && _b !== void 0 ? _b : definition.defaultSchedule) !== null && _c !== void 0 ? _c : "0 6 * * *";
    }
    cancelEditing() {
        this.editingReportType = null;
    }
    setFrequency(frequency) {
        this.frequency = frequency;
        if (frequency === "CUSTOM" && this.customCron.trim().length === 0) {
            this.customCron = this.cronPreview;
        }
    }
    setHour(hour) {
        this.hour = hour;
    }
    setWeekday(day) {
        this.weekday = day;
    }
    setDayOfMonth(day) {
        this.dayOfMonth = day;
    }
    setCustomCron(cron) {
        this.customCron = cron;
    }
    setEnabled(enabled) {
        this.enabled = enabled;
    }
    async save() {
        if (this.editingReportType === null)
            return;
        this.isSaving = true;
        this.errorMessage = null;
        try {
            await this.reportsAdminService.setSchedule(this.editingReportType, {
                cron: this.cronPreview.trim(),
                enabled: this.enabled,
            });
            runInAction(() => {
                this.editingReportType = null;
            });
            await this.load();
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
        finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
    async setScheduleEnabled(definition, enabled) {
        const existing = definition.schedule;
        if (existing === undefined)
            return;
        this.isSaving = true;
        this.errorMessage = null;
        try {
            await this.reportsAdminService.setSchedule(definition.type, {
                cron: existing.cron,
                enabled,
            });
            await this.load();
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
        finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
    async removeSchedule(definition) {
        this.isSaving = true;
        this.errorMessage = null;
        try {
            await this.reportsAdminService.deleteSchedule(definition.type);
            runInAction(() => {
                this.editingReportType = null;
            });
            await this.load();
        }
        catch (error) {
            runInAction(() => {
                this.errorMessage = describeError(error);
            });
        }
        finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
}
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "definitions", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "errorMessage", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "editingReportType", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "isSaving", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "frequency", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "hour", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "weekday", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "dayOfMonth", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "customCron", void 0);
__decorate([
    observable
], GlobalAdminReportSchedulesViewModel.prototype, "enabled", void 0);
__decorate([
    computed
], GlobalAdminReportSchedulesViewModel.prototype, "allReports", null);
__decorate([
    computed
], GlobalAdminReportSchedulesViewModel.prototype, "scheduledCount", null);
__decorate([
    computed
], GlobalAdminReportSchedulesViewModel.prototype, "activeCount", null);
__decorate([
    computed
], GlobalAdminReportSchedulesViewModel.prototype, "editingDefinition", null);
__decorate([
    computed
], GlobalAdminReportSchedulesViewModel.prototype, "cronPreview", null);
__decorate([
    computed
], GlobalAdminReportSchedulesViewModel.prototype, "summary", null);
__decorate([
    action
], GlobalAdminReportSchedulesViewModel.prototype, "startEditing", null);
__decorate([
    action
], GlobalAdminReportSchedulesViewModel.prototype, "cancelEditing", null);
__decorate([
    action
], GlobalAdminReportSchedulesViewModel.prototype, "setFrequency", null);
__decorate([
    action
], GlobalAdminReportSchedulesViewModel.prototype, "setHour", null);
__decorate([
    action
], GlobalAdminReportSchedulesViewModel.prototype, "setWeekday", null);
__decorate([
    action
], GlobalAdminReportSchedulesViewModel.prototype, "setDayOfMonth", null);
__decorate([
    action
], GlobalAdminReportSchedulesViewModel.prototype, "setCustomCron", null);
__decorate([
    action
], GlobalAdminReportSchedulesViewModel.prototype, "setEnabled", null);
function clampHour(hour) {
    return Math.min(23, Math.max(0, Math.trunc(hour)));
}
function clampDayOfMonth(day) {
    return Math.min(28, Math.max(1, Math.trunc(day)));
}
function describeError(error) {
    const message = readApiMessage(error);
    if (message !== null)
        return message;
    return error instanceof Error ? error.message : "Something went wrong";
}
function readApiMessage(error) {
    if (typeof error !== "object" || error === null || !("response" in error))
        return null;
    const response = Reflect.get(error, "response");
    if (typeof response !== "object" || response === null)
        return null;
    const data = Reflect.get(response, "data");
    if (typeof data !== "object" || data === null)
        return null;
    for (const key of ["message", "error"]) {
        const value = Reflect.get(data, key);
        if (typeof value === "string" && value.length > 0)
            return value;
    }
    return null;
}
